// Components
import { Feature } from '.generated/templates/Feature.Items.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import Button from 'components/helpers/Button/Button';
import {
  ButtonIconNames,
  ButtonIconPosition,
} from 'components/helpers/Icons/ButtonLinkIcon/ButtonLinkIcon';
import { HeroIconNames } from 'components/helpers/Icons/HeroIcon/HeroIcon';
import { getEnum } from 'lib/get-enum';
import { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import EditingHelpText, { MissingDataSource } from 'components/helpers/EditingHelpText';
import ContentMangedIcon from 'components/helpers/Icons/ContentMangedIcon/ContentMangedIcon';

export type CountdownTimerProps = Sitecore.Override.ComponentBase &
  Feature.Components.CountdownTimers.Fields.CountdownTimer & {
    nowOverride?: DateTime;
  };

type Timeleft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};
const CountdownTimer = ({ fields, nowOverride, rendering }: CountdownTimerProps): JSX.Element => {
  // Fail out if we don't have any fields
  /****DON'T use var, PREFER const , USE let only for changing variables***/
  if (!fields) {
    return <MissingDataSource />;
  }

  /***Enumhandler/Fields from Sitecore***/
  const heroIcon = getEnum<HeroIconNames>(fields?.icon);

  ///get image
  const buttonIcon = getEnum<ButtonIconNames>(fields?.primaryCtaIcon);
  const buttonIconPosition = getEnum<ButtonIconPosition>(fields?.primaryCtaIconLocation);
  const countdownDate = fields?.countdownDate.value;

  /***handlers***/
  const calculateTimeLeft = (): Timeleft => {
    const now = nowOverride || DateTime.local();

    const difference = +DateTime.fromISO(countdownDate) - +now;

    let timeleft = {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };

    if (difference > 0) {
      timeleft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }

    return timeleft;
  };

  /***Helper methods***/
  const addsZeroInFrontFormatter = (x: number) => {
    if (x < 10) {
      return '0' + x;
    } else {
      return x;
    }
  };

  /*state Mgmt hooks*/
  // const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [timeLeft, setTimeLeft] = useState<Timeleft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  /**** Component LifeCycles Hooks : Called after Component Mounts ***/
  useEffect(() => {
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();

      if (
        newTimeLeft.days <= 0 &&
        newTimeLeft.hours <= 0 &&
        newTimeLeft.minutes <= 0 &&
        newTimeLeft.seconds <= 0
      ) {
        clearInterval(timer);
      }

      setTimeLeft(newTimeLeft);
    }, 1000);

    /**Called After Component is removed**/

    return () => clearInterval(timer);
  }, []);

  if (countdownDate === '0001-01-01T00:00:00Z') {
    return <EditingHelpText condition={true} text={`Countdown Timer | No Countdown Date set. `} />;
  }

  const expired =
    timeLeft.days <= 0 && timeLeft.hours <= 0 && timeLeft.minutes <= 0 && timeLeft.seconds <= 0;
  //if timer is expired return blank
  if (expired) {
    return (
      <EditingHelpText
        condition={true}
        text={`Countdown Timer | Countdown Date "${DateTime.fromISO(countdownDate).toLocaleString(
          DateTime.DATETIME_SHORT,
          { locale: 'en-US' }
        )}" has passed. `}
      />
    );
  }
  const componentAnchorId = rendering?.uid;
  return (
    <div
      data-component="authorable/general/countdowntimer"
      className={classNames('anchorIdIndentifier')}
      id={componentAnchorId}
    >
      <LayoutPageSectionShell theme="theme-blue--peacock">
        {/*icon and title*/}
        <div
          className={classNames(
            'md:grid',
            'grid-cols-3',
            'md:gap-7',
            'lg:gap-10',
            'items-center',
            'py-6',
            'md:py-3'
          )}
        >
          <div className={classNames('col-span-1', 'mb-1', 'md:mb-0')}>
            <div className={classNames('flex', 'flex-row', 'justify-center', 'items-center')}>
              <ContentMangedIcon icon={heroIcon} size={'md'} className={classNames('px-1')} />
              <span>
                <Text
                  className={classNames('flex', 'text-md', 'md:text-lg')}
                  field={fields?.title}
                />
              </span>
            </div>
          </div>
          {/* countdowntimer */}
          <div className={classNames('col-span-1', 'mb-6', 'md:mb-0')}>
            <div className={classNames('flex', 'flex-row', 'justify-center')}>
              <div className={classNames('text-center')}>
                <div className={classNames('block', 'px-2')}>
                  <div className={classNames('text-xl', 'md:text-2xl')}>
                    {addsZeroInFrontFormatter(timeLeft.days)}
                  </div>
                  <div className={classNames('!text-sm')}>
                    <Text field={fields?.daysLabel} />
                  </div>
                </div>
              </div>

              <div className={classNames('text-2xl')}>:</div>

              <div className={classNames('text-center')}>
                <div className={classNames('block', 'px-2')}>
                  <div className={classNames('text-xl', 'md:text-2xl')}>
                    {addsZeroInFrontFormatter(timeLeft.hours)}
                  </div>
                  <div className={classNames('!text-sm')}>
                    <Text className={classNames('!text-sm')} field={fields?.hoursLabel} />
                  </div>
                </div>
              </div>

              <div className={classNames('text-2xl')}>:</div>

              <div className={classNames('text-center')}>
                <div className={classNames('block', 'px-2')}>
                  <div className={classNames('text-xl', 'md:text-2xl')}>
                    {addsZeroInFrontFormatter(timeLeft.minutes)}
                  </div>
                  <div className={classNames('!text-sm')}>
                    <Text field={fields?.minutesLabel} />
                  </div>
                </div>
              </div>

              <div className={classNames('text-2xl')}>:</div>

              <div className={classNames('text-center')}>
                <div className={classNames('block', 'px-2')}>
                  <div className={classNames('text-xl', 'md:text-2xl')}>
                    {addsZeroInFrontFormatter(timeLeft.seconds)}
                  </div>
                  <div className={classNames('!text-sm')}>
                    <Text field={fields?.secondsLabel} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* cta */}
          <div className={classNames('col-span-1', 'mb-0')}>
            <div className={classNames('flex', 'flex-row', 'justify-center')}>
              <Button
                size="small"
                className={classNames()}
                style={{ fontSize: '16px', width: '154px' }}
                icon={buttonIcon}
                iconPosition={buttonIconPosition}
                field={fields.primaryCta}
                variant={getEnum(fields.primaryCtaStyle)}
              />
            </div>
          </div>
        </div>
      </LayoutPageSectionShell>
    </div>
  );
};

export default CountdownTimer;
